import { FC } from 'react'
import { downloadAuthPrescricao } from '../../../../../public/prescricao/core/_request'
import { rotuloNumeroSncr } from '../../../../sncr/core/_models'
import { documentoDaPrescricao } from '../../core/montagem/historico'
import { downloadAPAC, downloadLME, downloadTermo } from '../../core/_requests'
import { DocumentoEmAssinatura } from '../../hooks/useAssinaturaDocumentos'
import { IconPaths } from '_metronic/helpers'
import styles from '../../Prescricao.module.scss'
import { MarcaEstado } from '../comum/MarcaEstado'
import { useRotulos } from '../comum/useRotulos'
import { ArquivoDto } from '../../../../atendimento/core/_models'
import { visualizarArquivo } from './arquivos'
import { Button } from 'app/components'

type Linha = {
  chave: string
  titulo: string
  assinado: boolean
  emAndamento?: boolean
  anexo?: boolean
  validade?: string | null
  sncr?: string
  codigo?: string
  buscar?: () => Promise<ArquivoDto>
}

const SUFIXO_ASSINADO = /\s*\(assinad[oa]\)\s*$/i

/**
 * O backend descreve o anexo já assinado com "(assinado)" no fim ("Formulário LME - X (assinado)").
 * O sufixo sai do título e vira o mesmo selo das linhas principais.
 */
const anexoDescrito = (descricao: string) => ({
  titulo: descricao.replace(SUFIXO_ASSINADO, ''),
  assinado: SUFIXO_ASSINADO.test(descricao),
})

/** Documentos da sessão com validade, SNCR, código de acesso e o PDF; anexos SUS logo abaixo. */
export const DocumentosAssinados: FC<{ documentos: DocumentoEmAssinatura[] }> = ({
  documentos,
}) => {
  const { t, tituloDocumento } = useRotulos()

  const linhas: Linha[] = documentos.flatMap(({ prescricao, estado }) => {
    const titulo = tituloDocumento(documentoDaPrescricao(prescricao))
    const assinado = estado === 'assinado'
    const principal: Linha = {
      chave: `p-${prescricao.id}`,
      titulo,
      assinado,
      emAndamento: estado === 'assinando',
      validade: assinado ? prescricao.dataValidadeFormatada : undefined,
      sncr: assinado
        ? rotuloNumeroSncr(prescricao.numeroSncr, prescricao.tipoReceitaSncr)
        : undefined,
      codigo: assinado ? prescricao.codigoAcesso : undefined,
      buscar: assinado
        ? () => downloadAuthPrescricao(prescricao.codigoAcesso, prescricao.codigoPaciente)
        : undefined,
    }
    const anexos: Linha[] = [
      ...(prescricao.lme
        ? [
            {
              chave: `lme-${prescricao.id}`,
              ...anexoDescrito(prescricao.lme.descricao || t('prescricao.selo.lme')),
              buscar: () => downloadLME(prescricao.id),
            },
          ]
        : []),
      ...(prescricao.apac
        ? [
            {
              chave: `apac-${prescricao.id}`,
              ...anexoDescrito(prescricao.apac.descricao || t('prescricao.selo.apac')),
              buscar: () => downloadAPAC(prescricao.id),
            },
          ]
        : []),
      ...(prescricao.documentos ?? []).map((documento) => ({
        chave: `doc-${prescricao.id}-${documento.id}`,
        ...anexoDescrito(documento.descricao || t('prescricao.resultado.termo')),
        buscar: () => downloadTermo(prescricao.id, documento.id),
      })),
    ].map((anexo) => ({ ...anexo, anexo: true }))
    return [principal, ...anexos]
  })

  return (
    <section className='card card-bordered' aria-labelledby='resultado-documentos'>
      <div className='card-header border-0 pt-5'>
        <h3 id='resultado-documentos' className='card-title fs-5 fw-bold'>
          {t('prescricao.resultado.documentos')}
        </h3>
      </div>
      <div className='card-body pt-0 table-responsive'>
        <table className='table table-row-bordered align-middle fs-7 m-0'>
          <thead>
            <tr className={`fw-bold fs-8 ${styles.textoSecundario}`}>
              <th scope='col'>{t('prescricao.resultado.documento')}</th>
              <th scope='col'>{t('prescricao.resultado.validade')}</th>
              <th scope='col'>{t('prescricao.resultado.sncr')}</th>
              <th scope='col'>{t('prescricao.resultado.codigo')}</th>
              <th scope='col' className='text-end'>
                {t('prescricao.resultado.acoes')}
              </th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr key={linha.chave}>
                <th scope='row' className='fw-semibold'>
                  <span className='d-flex flex-column'>
                    {linha.anexo && (
                      <span className='visually-hidden'>{t('prescricao.resultado.anexo')}</span>
                    )}
                    {linha.titulo}
                    {/* No anexo o selo só aparece quando o backend diz que ele está assinado. */}
                    {(!linha.anexo || linha.assinado) && (
                      // Mesmo selo da situação no histórico: ícone + texto, cor nunca sozinha.
                      // aria-live: "Assinando" → "Assinado" ao reassinar daqui é anunciado.
                      <span
                        aria-live='polite'
                        className={`${styles.selo} align-self-start mt-1 ${
                          linha.assinado
                            ? styles.seloApac
                            : linha.emAndamento
                            ? styles.seloNeutro
                            : styles.seloControle
                        }`}
                      >
                        <MarcaEstado
                          marca={
                            linha.assinado
                              ? IconPaths.Check
                              : linha.emAndamento
                              ? 'andamento'
                              : IconPaths.Alert
                          }
                        />
                        {linha.assinado
                          ? t('prescricao.assinatura.estado.assinado')
                          : linha.emAndamento
                          ? t('prescricao.assinatura.estado.assinando')
                          : t('prescricao.resultado.nao_assinado')}
                      </span>
                    )}
                  </span>
                </th>
                <td>{linha.validade || '—'}</td>
                <td className='text-nowrap'>{linha.sncr || '—'}</td>
                <td>
                  {linha.codigo ? <span className='font-monospace'>{linha.codigo}</span> : '—'}
                </td>
                <td>
                  {linha.buscar && (
                    <div className='d-flex justify-content-end'>
                      <Button
                        size='btn-sm'
                        className='btn-primary'
                        onClick={() => linha.buscar && visualizarArquivo(linha.buscar)}
                        ariaLabel={t('prescricao.resultado.visualizar_aria', {
                          titulo: linha.titulo,
                        })}
                      >
                        {t('common.view')}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
